import * as v from 'valibot';

import {
  branchSchema,
  carrierSchema,
  carrierServiceSchema,
  countrySchema,
  currencySchema,
  customerSchema,
  directInterlineSchema,
  equipmentTypeSchema,
  pricingTypeSchema,
  shipTypeSchema,
  stateSchema,
  tariffTypeSchema,
  transportTypeSchema,
} from '@/modules/entities';

const nullableString = v.nullable(v.string());

// TODO: Add export Option type to common entities

const option = <T extends v.GenericSchema>(id: T, message?: string) => v.object({ id, label: v.string() }, message);

// Accessorials list item (BE erase fields to null whenever they want)
export const accessorialApiSchema = v.object({
  accessorialAction: nullableString,
  accessorialActionDescription: nullableString,
  accessorialBasis: nullableString,
  accessorialBasisDescription: nullableString,
  accessorialCode: nullableString,
  accessorialKey: v.number(),
  accessorialName: nullableString,
  accessorialType: nullableString,
  accessorialTypeDescription: nullableString,
  automation: nullableString,
  branchDescription: nullableString,
  branchKey: v.number(),
  carrierKey: v.number(),
  carrierName: nullableString,
  createdBy: v.number(),
  createdByName: nullableString,
  createdOn: v.string(),
  currencyCode: nullableString,
  currencyName: nullableString,
  customerKey: v.number(),
  customerLaneID: nullableString,
  customerName: nullableString,
  destinationLocationKey: v.nullable(v.number()),
  directInterline: nullableString,
  directInterlineDescription: nullableString,
  effectiveFromDate: nullableString,
  effectiveToDate: nullableString,
  equipmentType: nullableString,
  freight: nullableString,
  freightRequired: nullableString,
  fuelSurchargeIndicator: nullableString,
  isDangerousGoods: v.nullable(v.boolean()),
  isVatable: nullableString,
  laneBased: nullableString,
  laneKey: v.nullable(v.number()),
  modifiedBy: v.nullable(v.number()),
  modifiedByName: nullableString,
  modifiedOn: nullableString,
  originLocationKey: v.nullable(v.number()),
  pricingType: nullableString,
  pricingTypeDescription: nullableString,
  providerLaneID: nullableString,
  reviewFlag: nullableString,
  serviceDescription: nullableString,
  serviceKey: v.number(),
  shipType: nullableString,
  shipTypeDescription: nullableString,
  trailerKey: v.number(),
  transportType: nullableString,
  transportTypeDescription: nullableString,
  validateDeliveryDate: nullableString,
});

export const accessorialsApiSchema = v.object({
  items: v.nullable(v.array(accessorialApiSchema)),
  totalCount: v.number(),
});

export const accessorialSchema = v.object({
  ...accessorialApiSchema.entries,

  accessorialBasis: v.nullable(tariffTypeSchema),
  accessorialType: v.string(),
  branch: branchSchema,
  carrier: carrierSchema,
  createdByName: v.string(),
  currency: v.nullable(currencySchema),
  customer: customerSchema,
  directInterline: v.nullable(directInterlineSchema),
  equipmentType: equipmentTypeSchema,
  modifiedByName: v.string(),
  pricingType: v.nullable(pricingTypeSchema),
  service: carrierServiceSchema,
  shipType: v.nullable(shipTypeSchema),
  transportType: v.nullable(transportTypeSchema),
});

export const accessorialsSchema = v.object({
  items: v.array(accessorialSchema),
  totalCount: v.number(),
});

export type AccessorialApi = v.InferOutput<typeof accessorialApiSchema>;
export type Accessorial = v.InferOutput<typeof accessorialSchema>;
export type AccessorialsApi = v.InferOutput<typeof accessorialsApiSchema>;
export type Accessorials = v.InferOutput<typeof accessorialsSchema>;

export const accessorialDetailsItemApiSchema = v.object({
  actualClass: v.string(),
  baseRate: v.number(),
  calculationType: v.string(),
  discount: v.number(),
  discountCalculationType: v.string(),
  id: v.string(),
  laneId: v.string(),
  range1: v.object({
    from: v.string(),
    measurement: v.string(),
    to: v.string(),
    type: v.string(),
  }),
});

export const accessorialDetailsApiSchema = v.object({
  items: v.array(accessorialDetailsItemApiSchema),
  total: v.number(),
});

export const accessorialDetailsParamsSchema = v.object({
  accessorialKey: v.optional(v.number()),
  limit: v.optional(v.pipe(v.number(), v.minValue(0)), 10),
  offset: v.optional(v.pipe(v.number(), v.minValue(0)), 0),
});

export type AccessorialDetailsApi = v.InferOutput<typeof accessorialDetailsApiSchema>;
export type AccessorialDetailsParams = v.InferOutput<typeof accessorialDetailsParamsSchema>;

export const DANGEROUS_GOODS_OPTIONS = [
  { id: 'true', label: 'Yes' },
  { id: 'false', label: 'No' },
] as const;

export type DangerousGoodsOption = (typeof DANGEROUS_GOODS_OPTIONS)[number];

/**
 * API contract for accessorials search endpoint, search query params.
 * Also used to validate URL search params on the client side.
 */
export const accessorialsFiltersApiSchema = v.object({
  accessorialCode: v.optional(v.string()),
  accessorialKey: v.optional(v.number()),
  branchKey: v.optional(v.number()),
  carrierKey: v.optional(v.number()),
  currencyCode: v.optional(v.string()),
  customerKey: v.number(),
  dangerousGoods: v.optional(v.boolean()),
  destinationCity: v.optional(v.string()),
  destinationCountry: v.optional(v.string()),
  destinationLocationKey: v.optional(v.number()),
  destinationPortName: v.optional(v.string()),
  destinationPostalFrom: v.optional(v.string()),
  destinationPostalTo: v.optional(v.string()),
  destinationState: v.optional(v.string()),
  directInterLine: v.optional(v.string()),
  effectiveFromDate: v.optional(v.pipe(v.string(), v.isoTimestamp())),
  effectiveToDate: v.optional(v.pipe(v.string(), v.isoTimestamp())),
  endEffectiveToDate: v.optional(v.pipe(v.string(), v.isoTimestamp())),
  laneKey: v.optional(v.number()),
  originCity: v.optional(v.string()),
  originCountry: v.optional(v.string()),
  originLocationKey: v.optional(v.number()),
  originPortName: v.optional(v.string()),
  originPostalFrom: v.optional(v.string()),
  originPostalTo: v.optional(v.string()),
  originState: v.optional(v.string()),
  pricingType: v.optional(v.string()),
  serviceKey: v.optional(v.number()),
  shipType: v.optional(v.string()),
  skip: v.optional(v.number()),
  sorting: v.optional(v.string()),
  startEffectiveFromDate: v.optional(v.pipe(v.string(), v.isoTimestamp())),
  take: v.optional(v.number()),
  trailerKey: v.optional(v.number()),
});

export type AccessorialsFiltersApi = v.InferInput<typeof accessorialsFiltersApiSchema>;

/**
 * Accessorials search filters form
 */
export const accessorialsFiltersSchema = v.pipe(
  v.object({
    accessorialCode: v.optional(v.nullable(option(v.string()))),
    accessorialKey: v.optional(v.nullable(v.string())),
    branch: v.optional(v.nullable(branchSchema)),
    carrier: v.optional(v.nullable(carrierSchema)),
    currency: v.optional(v.nullable(currencySchema)),
    destinationCity: v.optional(v.nullable(v.string())),
    destinationCountry: v.optional(v.nullable(countrySchema)),
    destinationLocationKey: v.optional(v.nullable(option(v.number()))),
    destinationPortKey: v.optional(v.nullable(option(v.number()))),
    destinationPostalFrom: v.optional(v.nullable(v.string())),
    destinationPostalTo: v.optional(v.nullable(v.string())),
    destinationState: v.optional(v.nullable(stateSchema)),
    directInterline: v.optional(v.nullable(directInterlineSchema)),
    effectiveFromDate: v.optional(v.pipe(v.string(), v.isoTimestamp('Please enter a valid date'))),
    effectiveToDate: v.optional(v.pipe(v.string(), v.isoTimestamp('Please enter a valid date'))),
    endEffectiveToDate: v.optional(v.pipe(v.string(), v.isoTimestamp('Please enter a valid date'))),
    equipmentType: v.optional(v.nullable(equipmentTypeSchema)),
    isDangerousGoods: v.optional(v.nullable(option(v.picklist(['true', 'false'])))),
    laneKey: v.optional(v.nullable(option(v.number()))),
    originCity: v.optional(v.nullable(v.string())),
    originCountry: v.optional(v.nullable(countrySchema)),
    originLocationKey: v.optional(v.nullable(option(v.number()))),
    originPortKey: v.optional(v.nullable(option(v.number()))),
    originPostalFrom: v.optional(v.nullable(v.string())),
    originPostalTo: v.optional(v.nullable(v.string())),
    originState: v.optional(v.nullable(stateSchema)),
    pricingType: v.optional(v.nullable(pricingTypeSchema)),
    service: v.optional(v.nullable(carrierServiceSchema)),
    shipType: v.optional(v.nullable(shipTypeSchema)),
    startEffectiveFromDate: v.optional(v.pipe(v.string(), v.isoTimestamp('Please enter a valid date'))),
  }),
  v.forward(
    v.check((data) => {
      if (!data.startEffectiveFromDate || !data.endEffectiveToDate) {
        return true;
      }

      return new Date(data.endEffectiveToDate) >= new Date(data.startEffectiveFromDate);
    }, 'To date should be bigger than From date'),
    ['endEffectiveToDate'],
  ),
  v.forward(
    v.check((data) => {
      if (!data.effectiveFromDate || !data.effectiveToDate) {
        return true;
      }

      return new Date(data.effectiveToDate) >= new Date(data.effectiveFromDate);
    }, 'To date should be bigger than From date'),
    ['effectiveToDate'],
  ),
);

export type AccessorialsFilters = v.InferOutput<typeof accessorialsFiltersSchema>;
